import { useState, type ReactNode } from "react";
import { Modal } from "@dearbill/shared";

export interface HelpWalkthroughProps {
  /** True once this terminal has been shown the walkthrough (org-scoped memory). */
  seenIntro: boolean;
  /** Persist the seen flag; called on every dismissal and idempotent. */
  onSeen: () => void;
  /** True while the plan step offers the plan-year toggle, which adds its line to "Good to know". */
  hasNextYear: boolean;
}

export interface HelpWalkthroughModalProps {
  open: boolean;
  onClose: () => void;
  hasNextYear: boolean;
}

/** The three counter-flow steps, in the page's own top-to-bottom order. */
const STEPS: readonly { title: string; body: string }[] = [
  {
    title: "Find patient's plan",
    body: "Select the patient's State, then enter their plan name or contract id from the insurance card (ex: H1234-001).",
  },
  {
    title: "Add patient's medications",
    body: "Start by typing the drug name. You'll see coverage and estimates.",
  },
  {
    title: "Refer patient",
    body: "Refer patients you think would benefit from a prescription affordability program.",
  },
];

const GOOD_TO_KNOW: readonly string[] = [
  "Estimates aren't quotes.",
  "The tool never stores patient names.",
  "Your recent plans and checks stay on this device only.",
];

/** The "Good to know" line for the plan-year toggle; it names no year. */
const PLAN_YEAR_TIP =
  "Switch the plan year to estimate next year's plan rules at this year's prices.";

/**
 * The walkthrough dialog as a controlled modal; the caller owns the `open`
 * state. Its one production caller is the `HelpWalkthrough` wrapper below,
 * with the trigger button and the first-run auto-open.
 *
 * The steps render as one numbered list to take in at a glance, not a
 * click-through wizard, because nobody at a pharmacy counter pages through
 * steps. A short "Good to know" list follows.
 */
export function HelpWalkthroughModal({
  open,
  onClose,
  hasNextYear,
}: HelpWalkthroughModalProps): ReactNode {
  const tips = hasNextYear ? [...GOOD_TO_KNOW, PLAN_YEAR_TIP] : GOOD_TO_KNOW;
  return (
    <Modal open={open} title="How this works" onClose={onClose} closeOnBackdrop>
      <ol className="space-y-4">
        {STEPS.map((step, i) => (
          <li key={step.title} className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="text-ember flex w-5 shrink-0 items-center justify-center text-xl font-semibold"
            >
              {i + 1}
            </span>
            <div className="min-w-0">
              <p className="text-foreground font-semibold">{step.title}</p>
              <p className="text-slate-teal mt-0.5 text-base leading-[22px]">{step.body}</p>
            </div>
          </li>
        ))}
      </ol>
      <div className="border-haze mt-5 border-t pt-4">
        <p className="text-foreground font-semibold">Good to know</p>
        <ul className="text-slate-teal mt-1 list-disc space-y-1 pl-5 text-base leading-[22px]">
          {tips.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}

/**
 * The "How this works" button in the Member cost share calculator header, which opens
 * `HelpWalkthroughModal`.
 *
 * On the first visit per org (the `seenIntro` flag in `useCostToolMemory`) the
 * walkthrough opens by itself, once. Any dismissal (X, Escape, backdrop click)
 * persists the flag through `onSeen`, and the button stays in the header for
 * reopening.
 */
export function HelpWalkthrough({
  seenIntro,
  onSeen,
  hasNextYear,
}: HelpWalkthroughProps): ReactNode {
  // The flag seeds only the initial state: it flips to true on the first
  // dismissal, and that flip must not reopen or close anything mid-session.
  const [open, setOpen] = useState(!seenIntro);

  const close = (): void => {
    setOpen(false);
    onSeen();
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-ember hover:text-ember-dark inline-flex min-h-11 cursor-pointer items-center font-semibold underline underline-offset-2"
      >
        <QuestionCircleIcon size={18} className="mr-1.5 shrink-0" />
        How this works
      </button>
      <HelpWalkthroughModal open={open} onClose={close} hasNextYear={hasNextYear} />
    </>
  );
}

/**
 * Question-mark-in-circle icon, local because the shared `icons.tsx` has none.
 * It is aria-hidden; the button's text is the label.
 */
function QuestionCircleIcon({
  size,
  className,
}: {
  size: number;
  className?: string | undefined;
}): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </svg>
  );
}
